import React from 'react';
import { Alert, Textarea } from '@parsina/framework';

import { useTranslation } from '../../i18n';
import TaskDialog from '../../components/TaskDialog';

const REASON_MAX = 300;
const REASON_MIN = 3;

/**
 * Suspend a clinic's subscription.
 *
 * The reason is MANDATORY because a suspension outranks everything — the clinic
 * cannot clear it by paying — so the only record of why it happened is what the
 * operator types here. That text is shown back on the clinic's own record and in
 * the audit trail, which is exactly why it cannot be a placeholder that vanishes
 * on first keystroke (the previous shape) and cannot be lost when the mutation
 * fails (also the previous shape: the dialog closed before the request resolved).
 *
 * The confirm is destructive-styled and VERB-labelled — «تعلیق کن», not «تأیید» —
 * per §5.2: a confirm button that says "OK" makes the operator re-read the title
 * to find out what they are agreeing to.
 */
export default function SuspendDialog({ open, onOpenChange, pending, error, onSubmit }) {
  const { t } = useTranslation();
  const [reason, setReason] = React.useState('');

  React.useEffect(() => { if (open) setReason(''); }, [open]);

  const trimmed = reason.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < REASON_MIN;

  return (
    <TaskDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('clinic.dialogs.suspend.title')}
      error={error}
      pending={pending}
      dirty={!!reason}
      submitLabel={t('clinic.dialogs.suspend.submit')}
      submitVariant="destructive"
      submitDisabled={trimmed.length < REASON_MIN}
      onSubmit={() => onSubmit({ reason: trimmed })}
      data-testid="detail-suspend-confirm"
      lead={(
        <Alert variant="error">{t('clinic.dialogs.suspend.warning')}</Alert>
      )}
    >
      <div data-testid="detail-suspend-reason">
        <Textarea
          label={t('clinic.dialogs.suspend.reason')}
          required
          value={reason}
          onChange={(e) => setReason(e.target?.value ?? e)}
          placeholder={t('clinic.dialogs.suspend.reasonPlaceholder')}
          error={tooShort ? t('validation.tooShort', { min: REASON_MIN }) : undefined}
          maxLength={REASON_MAX}
          showCount
          rows={3}
        />
      </div>
    </TaskDialog>
  );
}
